import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Settings, FileText, Sparkles, Link2 as LinkIcon, CornerDownRight,
  Search, Eye, Download, Play, Pause, Trash2, Copy, RefreshCw,
  CheckCircle2, XCircle, Clock, Loader2, Upload, X, Image as ImageIcon, Mail
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { sitesAPI, pagesAPI, aiAPI, seoAPI, exportAPI, uploadAPI } from '@/lib/api';
import api from '@/lib/api';
import { formatDate, statusBadgeClass, statusLabel } from '@/lib/utils';
import { toast } from 'sonner';

export default function SiteDetailPage() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [pages, setPages] = useState([]);
  const [aiRuns, setAiRuns] = useState([]);
  const [seoAudit, setSeoAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);
  const [emailInfra, setEmailInfra] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [siteRes, pagesRes, aiRes] = await Promise.all([
        sitesAPI.get(siteId),
        pagesAPI.list(siteId),
        aiAPI.status(siteId),
      ]);
      setSite(siteRes.data);
      setPages(pagesRes.data);
      setAiRuns(aiRes.data);
    } catch (err) {
      toast.error('Veri yuklenemedi');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadSEO = useCallback(async () => {
    try {
      const { data } = await seoAPI.audit(siteId);
      setSeoAudit(data);
    } catch (err) {
      console.error(err);
    }
  }, [siteId]);

  useEffect(() => {
    if (activeTab === 'seo') loadSEO();
    if (activeTab === 'email') loadEmailInfra();
  }, [activeTab, loadSEO]);

  const loadEmailInfra = async () => {
    try {
      const { data } = await api.get(`/sites/${siteId}/email-infra`);
      setEmailInfra(data);
    } catch(e) { setEmailInfra({ status: 'not_created' }); }
  };

  const handleGenerate = async (type) => {
    try {
      setGenerating(true);
      const { data } = await aiAPI.generate(siteId, type);
      toast.success(`${data.message} (${data.run_id.slice(0,8)})`);
      // Poll for status
      const poll = setInterval(async () => {
        const res = await aiAPI.status(siteId);
        setAiRuns(res.data);
        const latest = res.data[0];
        if (latest && (latest.status === 'completed' || latest.status === 'failed')) {
          clearInterval(poll);
          setGenerating(false);
          if (latest.status === 'completed') {
            toast.success(`Icerik uretimi tamamlandi! ${latest.pages_generated} sayfa uretildi.`);
            const pRes = await pagesAPI.list(siteId);
            setPages(pRes.data);
            const sRes = await sitesAPI.get(siteId);
            setSite(sRes.data);
          } else {
            toast.error(`Uretim basarisiz: ${latest.error_message}`);
          }
        }
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Uretim basarilamadi');
      setGenerating(false);
    }
  };

  const handleSaveSite = async () => {
    if (!site) return;
    setSaving(true);
    try {
      await sitesAPI.update(siteId, {
        brand_name: site.brand_name,
        domain: site.domain,
        affiliate_url: site.affiliate_url,
        fallback_affiliate_url: site.fallback_affiliate_url,
        main_offer: site.main_offer,
        brand_colors: site.brand_colors,
        logo_url: site.logo_url,
      });
      toast.success('Site kaydedildi');
    } catch (err) {
      toast.error('Kaydetme basarisiz');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Gecersiz dosya tipi. PNG, JPG, WebP, SVG veya GIF yukleyin.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu 5MB\'dan buyuk olamaz.');
      return;
    }
    setLogoUploading(true);
    try {
      const { data } = await uploadAPI.logo(file);
      setSite({ ...site, logo_url: data.logo_url });
      // Auto-save logo_url
      await sitesAPI.update(siteId, { logo_url: data.logo_url });
      toast.success('Logo yuklendi ve kaydedildi!');
    } catch (err) {
      toast.error('Logo yuklenemedi');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    setSite({ ...site, logo_url: '' });
    await sitesAPI.update(siteId, { logo_url: '' });
    toast.success('Logo kaldirildi');
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handlePublishPage = async (pageId) => {
    try {
      await pagesAPI.publish(siteId, pageId);
      toast.success('Sayfa yayinlandi');
      const pRes = await pagesAPI.list(siteId);
      setPages(pRes.data);
    } catch (err) {
      toast.error('Yayinlama basarisiz');
    }
  };

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 rounded-xl shimmer" />)}</div>;
  }

  if (!site) {
    return <div className="text-center py-20 text-muted-foreground">Site bulunamadi</div>;
  }

  const latestRun = aiRuns[0];
  const isRunning = latestRun?.status === 'running';

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/sites')} className="gap-2 text-muted-foreground">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold tracking-tight font-['Space_Grotesk']">{site.brand_name}</h1>
              <span className={`text-[11px] px-2.5 py-1 rounded-full ${statusBadgeClass(site.status)}`}>
                {statusLabel(site.status)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground font-mono">{site.domain}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/preview/${siteId}`}>
            <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground"><Eye className="w-4 h-4" /> Onizle</Button>
          </Link>
          <a href={exportAPI.download(siteId)}>
            <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground"><Download className="w-4 h-4" /> Disa Aktar</Button>
          </a>
        </div>
      </motion.div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="site-detail-tabs">
        <TabsList className="bg-secondary/40 border border-border/70 rounded-xl p-1">
          <TabsTrigger value="settings" data-testid="site-detail-tab-settings" className="gap-1.5 text-xs"><Settings className="w-3.5 h-3.5" /> Ayarlar</TabsTrigger>
          <TabsTrigger value="pages" data-testid="site-detail-tab-pages" className="gap-1.5 text-xs"><FileText className="w-3.5 h-3.5" /> Sayfalar</TabsTrigger>
          <TabsTrigger value="ai" data-testid="site-detail-tab-ai" className="gap-1.5 text-xs"><Sparkles className="w-3.5 h-3.5" /> AI</TabsTrigger>
          <TabsTrigger value="affiliate" data-testid="site-detail-tab-affiliate" className="gap-1.5 text-xs"><LinkIcon className="w-3.5 h-3.5" /> Affiliate</TabsTrigger>
          <TabsTrigger value="redirect" data-testid="site-detail-tab-redirect" className="gap-1.5 text-xs"><CornerDownRight className="w-3.5 h-3.5" /> Redirect</TabsTrigger>
          <TabsTrigger value="seo" data-testid="site-detail-tab-seo" className="gap-1.5 text-xs"><Search className="w-3.5 h-3.5" /> SEO</TabsTrigger>
          <TabsTrigger value="email" data-testid="site-detail-tab-email" className="gap-1.5 text-xs"><Mail className="w-3.5 h-3.5" /> Email</TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <Card className="lg:col-span-8 border-border/70">
              <CardHeader><CardTitle className="text-base">Site Ayarlari</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Marka Adi</Label>
                    <Input value={site.brand_name} onChange={(e) => setSite({...site, brand_name: e.target.value})} className="mt-1.5 bg-secondary/40 border-border/70" />
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Domain</Label>
                    <Input value={site.domain} onChange={(e) => setSite({...site, domain: e.target.value})} className="mt-1.5 bg-secondary/40 border-border/70" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Ana Teklif</Label>
                  <Input value={site.main_offer || ''} onChange={(e) => setSite({...site, main_offer: e.target.value})} className="mt-1.5 bg-secondary/40 border-border/70" />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={handleSaveSite} disabled={saving} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] press-active">
                    {saving ? 'Kaydediliyor...' : 'Kaydet'}
                  </Button>
                </div>
              </CardContent>
            </Card>
            <Card className="lg:col-span-4 border-border/70">
              <CardHeader><CardTitle className="text-base">Marka Logosu</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {site.logo_url ? (
                    <div className="relative group">
                      <div className="w-full h-24 rounded-lg bg-secondary/60 border border-border/70 flex items-center justify-center overflow-hidden p-3">
                        <img
                          src={site.logo_url.startsWith('http') ? site.logo_url : site.logo_url}
                          alt="Logo"
                          className="max-h-full max-w-full object-contain"
                          data-testid="site-detail-logo-preview"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="absolute top-2 right-2 p-1 rounded-full bg-destructive/90 text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                        data-testid="site-detail-remove-logo"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="site-logo-upload"
                      className="flex flex-col items-center justify-center w-full h-24 rounded-lg border-2 border-dashed border-border/70 hover:border-primary/50 bg-secondary/30 cursor-pointer transition-colors duration-150"
                      data-testid="site-detail-logo-upload-area"
                    >
                      {logoUploading ? (
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          <span className="text-xs text-muted-foreground">Yukleniyor...</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1.5">
                          <Upload className="w-4 h-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Logo Yukle</span>
                          <span className="text-[10px] text-muted-foreground/60">PNG, JPG, SVG (max 5MB)</span>
                        </div>
                      )}
                    </label>
                  )}
                  <input
                    ref={logoInputRef}
                    id="site-logo-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  {site.logo_url && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => logoInputRef.current?.click()}
                      data-testid="site-detail-change-logo"
                    >
                      <ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Logo Degistir
                    </Button>
                  )}
                  <p className="text-[10px] text-muted-foreground/70">
                    Logo, siteye ve AI gorsel uretiminde kullanilacak.
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="lg:col-span-4 border-border/70">
              <CardHeader><CardTitle className="text-base">Bilgi</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Durum</span><span className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(site.status)}`}>{statusLabel(site.status)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Sayfalar</span><span>{site.pages_count || 0}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Son Guncelleme</span><span className="text-xs">{formatDate(site.updated_at)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Olusturulma</span><span className="text-xs">{formatDate(site.created_at)}</span></div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Pages Tab */}
        <TabsContent value="pages" className="mt-6">
          <Card className="border-border/70">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Sayfalar ({pages.length})</CardTitle>
              {pages.length < 13 && (
                <Button size="sm" variant="outline" className="text-xs border-border/70" onClick={async () => { try { const {data} = await sitesAPI.seedPages(siteId); toast.success(data.message); const pRes = await pagesAPI.list(siteId); setPages(pRes.data); } catch(e) { toast.error('Olusturulamadi'); }}}>
                  <FileText className="w-3 h-3 mr-1" /> Eksik Sayfalari Olustur
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {pages.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground mb-3">Henuz sayfa yok.</p>
                  <div className="flex gap-2 justify-center">
                    <Button onClick={async () => { try { const {data} = await sitesAPI.seedPages(siteId); toast.success(data.message); const pRes = await pagesAPI.list(siteId); setPages(pRes.data); const sRes = await sitesAPI.get(siteId); setSite(sRes.data); } catch(e) { toast.error('Sayfa olusturulamadi'); }}} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                      <FileText className="w-4 h-4 mr-2" /> 13 Sayfa Olustur
                    </Button>
                    <Button onClick={() => setActiveTab('ai')} variant="outline" className="border-border/70">
                      <Sparkles className="w-4 h-4 mr-2" /> AI ile Uret
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {pages.map((page) => (
                    <div key={page.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary/30 transition-colors duration-150">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-[hsl(var(--primary))]">{page.slug}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusBadgeClass(page.status)}`}>{statusLabel(page.status)}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">{page.seo_title || 'Baslik yok'}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Link to={`/preview/${siteId}?slug=${encodeURIComponent(page.slug)}`}>
                          <Button variant="ghost" size="icon" className="h-8 w-8"><Eye className="w-4 h-4" /></Button>
                        </Link>
                        {page.status !== 'published' && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-400" onClick={() => handlePublishPage(page.id)}>
                            <Play className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Tab */}
        <TabsContent value="ai" className="mt-6">
          <div className="space-y-6">
            {/* AI Content Generation */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <Card className="lg:col-span-8 border-border/70">
                <CardHeader><CardTitle className="text-base">AI Icerik Uretimi</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    AI motorunu kullanarak site icerigi uretin. Primary: Azure GPT 5.4 Pro, Fallback: Claude Opus 4.6
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button onClick={() => handleGenerate('homepage')} disabled={generating || isRunning} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] press-active" data-testid="ai-generation-start-button">
                      {(generating || isRunning) ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uretiliyor...</> : <><Sparkles className="w-4 h-4 mr-2" /> Ana Sayfa Uret</>}
                    </Button>
                    <Button onClick={() => handleGenerate('seo_pages')} disabled={generating || isRunning} variant="outline" className="border-border/70">
                      {(generating || isRunning) ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uretiliyor...</> : <><FileText className="w-4 h-4 mr-2" /> SEO Sayfalari Uret</>}
                    </Button>
                    <Button onClick={() => handleGenerate('legal_pages')} disabled={generating || isRunning} variant="outline" className="border-border/70">
                      Legal Sayfalar Uret
                    </Button>
                    <Button onClick={() => handleGenerate('all')} disabled={generating || isRunning} variant="outline" className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))]">
                      Tum Sayfalari Uret
                    </Button>
                  </div>
                </CardContent>
              </Card>
              <Card className="lg:col-span-4 border-border/70">
                <CardHeader><CardTitle className="text-base">Uretim Gecmisi</CardTitle></CardHeader>
                <CardContent>
                  {aiRuns.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Henuz uretim yapilmadi</p>
                  ) : (
                    <div className="space-y-3">
                      {aiRuns.slice(0, 5).map((run) => (
                        <div key={run.id} className="flex items-start gap-3 p-2 rounded-lg">
                          {run.status === 'completed' ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" /> :
                           run.status === 'running' ? <Loader2 className="w-4 h-4 text-amber-400 animate-spin mt-0.5" /> :
                           run.status === 'failed' ? <XCircle className="w-4 h-4 text-red-400 mt-0.5" /> :
                           <Clock className="w-4 h-4 text-muted-foreground mt-0.5" />}
                          <div className="min-w-0">
                            <p className="text-xs font-medium">{run.prompt_type === 'all' ? 'Tum Sayfalar' : run.prompt_type === 'homepage' ? 'Ana Sayfa' : run.prompt_type === 'seo_pages' ? 'SEO Sayfalari' : 'Legal'}</p>
                          <p className="text-[10px] text-muted-foreground">{formatDate(run.created_at)} - {run.pages_generated || 0} sayfa</p>
                          {run.error_message && <p className="text-[10px] text-red-400 mt-0.5">{run.error_message}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

            {/* AI Visual Factory - Image Generation */}
            <Card className="border-border/70">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Gorsel Uretimi (AI Visual Factory v2)</CardTitle>
                <Button 
                  onClick={async () => {
                    try {
                      toast.info('Tum sayfalar icin gorsel uretimi baslatildi...');
                      await api.post(`/sites/${siteId}/generate-images`, {});
                      toast.success('Gorsel uretimi arka planda baslatildi! Her sayfa ~30-60sn surecek.');
                      setTimeout(async () => { const pRes = await pagesAPI.list(siteId); setPages(pRes.data); }, 60000);
                    } catch(e) { toast.error('Gorsel uretimi baslatılamadı'); }
                  }}
                  className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] gap-2 press-active text-xs"
                >
                  <Sparkles className="w-3.5 h-3.5" /> Tum Gorselleri Uret
                </Button>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-4">
                  Diversity Engine + GPT Prompt Builder + OpenAI gpt-image-1 + Pillow Compose. Her sayfa icin benzersiz gorsel uretilir.
                </p>
                {pages.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Once sayfa uretin, sonra gorsel olusturabilirsiniz.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pages.map((page) => (
                      <div key={page.id} className="border border-border/70 rounded-xl overflow-hidden group">
                        {/* Image Preview */}
                        <div className="aspect-video bg-secondary/30 relative">
                          {page.featured_image ? (
                            <img src={page.featured_image} alt={page.seo_title || page.slug} className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground/30">
                              <FileText className="w-8 h-8" />
                            </div>
                          )}
                          {/* Hover overlay with regenerate */}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <Button 
                              size="sm" 
                              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-xs gap-1"
                              onClick={async () => {
                                try {
                                  toast.info(`${page.slug} icin gorsel uretiliyor...`);
                                  const { data } = await api.post(`/sites/${siteId}/pages/${page.id}/generate-image`, { visual_type: 'hero', layout: 'hero_left_text_right_visual' });
                                  toast.success(`Gorsel uretildi!`);
                                  const pRes = await pagesAPI.list(siteId); setPages(pRes.data);
                                } catch(e) { toast.error('Uretilemedi'); }
                              }}
                            >
                              <Sparkles className="w-3 h-3" /> Uret
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-xs gap-1 border-white/30 text-white"
                              onClick={async () => {
                                try {
                                  toast.info(`${page.slug} icin farkli layout...`);
                                  const { data } = await api.post(`/sites/${siteId}/pages/${page.id}/generate-image`, { visual_type: 'trust', layout: 'trust_score_layout' });
                                  toast.success(`Alternatif gorsel uretildi!`);
                                  const pRes = await pagesAPI.list(siteId); setPages(pRes.data);
                                } catch(e) { toast.error('Uretilemedi'); }
                              }}
                            >
                              Alternatif
                            </Button>
                          </div>
                        </div>
                        {/* Page info + Diversity Debug */}
                        <div className="p-3">
                          <p className="font-mono text-xs text-[hsl(var(--primary))]">{page.slug}</p>
                          <p className="text-[10px] text-muted-foreground truncate mt-0.5">{page.featured_image ? 'Gorsel mevcut' : 'Gorsel uretilmedi'}</p>
                          {page.image_metadata?.diversity_debug && (
                            <div className="mt-2 p-2 rounded bg-secondary/40 border border-border/50 space-y-0.5">
                              <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Prompt Debug</p>
                              <p className="text-[9px] text-muted-foreground"><span className="text-[hsl(var(--primary))]">Scene:</span> {page.image_metadata.diversity_debug.scene_type?.slice(0, 40)}</p>
                              <p className="text-[9px] text-muted-foreground"><span className="text-[hsl(var(--primary))]">Mood:</span> {page.image_metadata.diversity_debug.visual_mood}</p>
                              <p className="text-[9px] text-muted-foreground"><span className="text-[hsl(var(--primary))]">Comp:</span> {page.image_metadata.diversity_debug.composition_style?.slice(0, 30)}</p>
                              <p className="text-[9px] text-muted-foreground"><span className="text-[hsl(var(--primary))]">Light:</span> {page.image_metadata.diversity_debug.lighting_style?.slice(0, 30)}</p>
                              <p className="text-[9px] text-muted-foreground"><span className="text-[hsl(var(--primary))]">Signature:</span> {page.image_metadata.diversity_debug.style_signature?.slice(0, 35)}</p>
                              <p className="text-[9px] text-muted-foreground"><span className="text-[hsl(var(--primary))]">Similarity:</span> {page.image_metadata.diversity_debug.similarity_score ?? 'N/A'}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="affiliate" className="mt-6">
          <Card className="border-border/70 max-w-2xl">
            <CardHeader><CardTitle className="text-base">Affiliate Link Yonetimi</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm text-muted-foreground">Ana Affiliate URL</Label>
                <Input value={site.affiliate_url || ''} onChange={(e) => setSite({...site, affiliate_url: e.target.value})} className="mt-1.5 bg-secondary/40 border-border/70 font-mono text-xs" />
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Yedek Affiliate URL</Label>
                <Input value={site.fallback_affiliate_url || ''} onChange={(e) => setSite({...site, fallback_affiliate_url: e.target.value})} className="mt-1.5 bg-secondary/40 border-border/70 font-mono text-xs" />
              </div>
              <Button onClick={handleSaveSite} disabled={saving} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] press-active">
                {saving ? 'Kaydediliyor...' : 'Affiliate Linklerini Guncelle'}
              </Button>
              <p className="text-xs text-muted-foreground">Affiliate linki guncellendiginde tum CTA butonlari otomatik guncellenir.</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Redirect Tab */}
        <TabsContent value="redirect" className="mt-6">
          <Card className="border-border/70 max-w-2xl">
            <CardHeader><CardTitle className="text-base">Redirect Kurallari</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-muted-foreground">
                <p className="mb-3">Varsayilan: CTA buton ile yonlendirme (anlik redirect yok, SEO guvenilirlik icin)</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {['cta_only', 'delayed', 'exit_intent'].map((type) => (
                  <div key={type} className={`p-3 rounded-lg border cursor-pointer transition-colors duration-150 ${site.redirect_rules?.type === type ? 'border-[hsl(var(--primary))]/50 bg-secondary/40' : 'border-border/70 hover:border-border'}`}
                    onClick={() => setSite({...site, redirect_rules: {...(site.redirect_rules || {}), type}})}
                  >
                    <p className="text-sm font-medium">{type === 'cta_only' ? 'Sadece CTA' : type === 'delayed' ? 'Gecikmeli' : 'Cikis Niyeti'}</p>
                    <p className="text-xs text-muted-foreground mt-1">{type === 'cta_only' ? 'Kullanici CTA tiklar' : type === 'delayed' ? 'X saniye sonra redirect' : 'Sayfa kapatmaya calisinca'}</p>
                  </div>
                ))}
              </div>
              <Button onClick={handleSaveSite} disabled={saving} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] press-active">
                Kaydet
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo" className="mt-6">
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="border-border/70">
                <CardContent className="pt-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Toplam Sayfa</p>
                  <p className="text-2xl font-semibold mt-1">{seoAudit?.total_pages || 0}</p>
                </CardContent>
              </Card>
              <Card className="border-border/70">
                <CardContent className="pt-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Sorunlu Sayfa</p>
                  <p className="text-2xl font-semibold mt-1 text-amber-400">{seoAudit?.pages_with_issues || 0}</p>
                </CardContent>
              </Card>
              <Card className="border-border/70">
                <CardContent className="pt-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Ic Linkler</p>
                  <p className="text-2xl font-semibold mt-1">{seoAudit?.internal_links_count || 0}</p>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/70">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">SEO Denetimi</CardTitle>
                <Button onClick={loadSEO} variant="ghost" size="sm"><RefreshCw className="w-4 h-4" /></Button>
              </CardHeader>
              <CardContent>
                {!seoAudit || seoAudit.items?.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4">Sorun bulunamadi veya henuz denetim yapilmadi.</p>
                ) : (
                  <div className="space-y-2">
                    {seoAudit.items.map((item, idx) => (
                      <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg border-l-2 ${item.severity === 'critical' ? 'border-l-red-400 bg-red-400/5' : item.severity === 'warning' ? 'border-l-amber-400 bg-amber-400/5' : 'border-l-blue-400 bg-blue-400/5'}`}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-muted-foreground">{item.page_slug}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${item.severity === 'critical' ? 'bg-red-400/10 text-red-400' : item.severity === 'warning' ? 'bg-amber-400/10 text-amber-400' : 'bg-blue-400/10 text-blue-400'}`}>{item.severity === 'critical' ? 'Kritik' : item.severity === 'warning' ? 'Uyari' : 'Bilgi'}</span>
                          </div>
                          <p className="text-sm mt-1">{item.message}</p>
                          {item.suggestion && <p className="text-xs text-muted-foreground mt-1">{item.suggestion}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <a href={seoAPI.sitemap(siteId)} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="border-border/70">Sitemap.xml</Button>
              </a>
              <a href={seoAPI.robots(siteId)} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="border-border/70">Robots.txt</Button>
              </a>
            </div>
          </div>
        </TabsContent>

        {/* Email Infrastructure Tab */}
        <TabsContent value="email" className="mt-6">
          <Card className="border-border/70">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Email Infrastructure</CardTitle>
              <div className="flex gap-2">
                {!emailInfra || emailInfra.status === 'not_created' ? (
                  <Button size="sm" className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-xs gap-1" data-testid="create-email-infra-btn"
                    disabled={emailLoading}
                    onClick={async () => {
                      setEmailLoading(true);
                      try {
                        const { data } = await api.post(`/sites/${siteId}/email-infra/create`);
                        setEmailInfra(data.email_config);
                        toast.success('Email infra olusturuldu!');
                      } catch(e) { toast.error('Olusturulamadi'); }
                      setEmailLoading(false);
                    }}>
                    {emailLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Olustur
                  </Button>
                ) : (
                  <>
                    <Button size="sm" variant="outline" className="text-xs gap-1"
                      onClick={async () => {
                        try {
                          const { data } = await api.post(`/sites/${siteId}/email-infra/sync`);
                          if (data.data) setEmailInfra(prev => ({...prev, ...data.data}));
                          toast.success('Sync tamamlandi');
                        } catch(e) { toast.error('Sync basarisiz'); }
                      }}>
                      <RefreshCw className="w-3 h-3" /> Sync
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs gap-1"
                      onClick={async () => {
                        try {
                          await api.post(`/sites/${siteId}/email-infra/verify`);
                          toast.success('DNS dogrulama baslatildi');
                        } catch(e) { toast.error(e.response?.data?.detail || 'Dogrulama basarisiz'); }
                      }}>
                      <CheckCircle2 className="w-3 h-3" /> Verify DNS
                    </Button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!emailInfra || emailInfra.status === 'not_created' ? (
                <div className="text-center py-8">
                  <Mail className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-sm text-muted-foreground">Email infra henuz olusturulmadi</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">Olustur butonuna basarak mail subdomain, tracking ve DNS kayitlarini hazirlayin</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Status Badge */}
                  <div className="flex items-center gap-3">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      emailInfra.status === 'active' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' :
                      emailInfra.status === 'verified' ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30' :
                      emailInfra.status === 'pending_dns' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30' :
                      emailInfra.status === 'danger' ? 'bg-red-500/15 text-red-400 border border-red-500/30' :
                      'bg-secondary/50 text-muted-foreground border border-border/50'
                    }`} data-testid="email-status-badge">{emailInfra.status}</span>
                    {emailInfra.verification_status && <span className="text-[10px] text-muted-foreground">Verification: {emailInfra.verification_status}</span>}
                    {emailInfra.warmup_day > 0 && <span className="text-[10px] text-muted-foreground">Warmup Day {emailInfra.warmup_day}</span>}
                  </div>

                  {/* Subdomain Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      ['Mail Subdomain', emailInfra.mail_subdomain],
                      ['Tracking', emailInfra.tracking_subdomain],
                      ['From Email', emailInfra.from_email],
                      ['Reply-To', emailInfra.reply_to],
                    ].map(([label, value]) => (
                      <div key={label} className="flex items-center justify-between p-2.5 rounded-lg bg-secondary/30 border border-border/50">
                        <div>
                          <p className="text-[10px] text-muted-foreground">{label}</p>
                          <p className="text-xs font-mono">{value}</p>
                        </div>
                        <button className="p-1 rounded hover:bg-secondary/60 transition-colors" onClick={() => { navigator.clipboard.writeText(value); toast.success('Kopyalandi'); }}>
                          <Copy className="w-3 h-3 text-muted-foreground" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Stats */}
                  {emailInfra.stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        ['Gunluk Limit', emailInfra.daily_limit],
                        ['Bugun Gonderilen', emailInfra.stats.sent_today],
                        ['Health', emailInfra.health_score || 0],
                        ['Bounce', `${emailInfra.stats.bounce_rate || 0}%`],
                      ].map(([label, value]) => (
                        <div key={label} className="p-2.5 rounded-lg bg-secondary/30 border border-border/50 text-center">
                          <p className="text-lg font-bold">{value}</p>
                          <p className="text-[10px] text-muted-foreground">{label}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* DNS Records */}
                  {emailInfra.dns_records && emailInfra.dns_records.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold">DNS Kayitlari</h4>
                        <Button size="sm" variant="ghost" className="text-[10px] gap-1 h-7"
                          onClick={() => {
                            const text = emailInfra.dns_records.map(r => `${r.type}\t${r.host}\t${r.value}`).join('\n');
                            navigator.clipboard.writeText(text);
                            toast.success('Tum DNS kayitlari kopyalandi');
                          }}>
                          <Copy className="w-3 h-3" /> Tumunu Kopyala
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {emailInfra.dns_records.map((r, i) => (
                          <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg bg-secondary/20 border border-border/30 group">
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-primary/10 text-primary">{r.type}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] text-muted-foreground">{r.purpose}</p>
                              <p className="text-xs font-mono truncate" title={r.host}>Host: {r.host}</p>
                              <p className="text-xs font-mono truncate" title={r.value}>Value: {r.value}</p>
                            </div>
                            <button className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-secondary/60 transition-all"
                              onClick={() => { navigator.clipboard.writeText(r.value); toast.success(`${r.purpose} kopyalandi`); }}>
                              <Copy className="w-3 h-3 text-muted-foreground" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <p className="text-[9px] text-muted-foreground/50 mt-2 italic">GoDaddy: Host alanina sadece subdomain kismi (mail, click, s1._domainkey) yazin. Tam domain yazmayin.</p>
                    </div>
                  )}

                  {/* Mock mode warning */}
                  {emailInfra.ang_digital_id?.startsWith('mock_') && (
                    <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-xs text-amber-400">
                      <strong>Mock Mode:</strong> ANG Digital API bagli degil. DNS kayitlari preview olarak gosteriliyor. Gercek register icin ANG_DIGITAL_API_URL ve ANG_DIGITAL_API_KEY set edin.
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
